package org.example.project.screens_driver

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.*
import org.example.project.engineletter.RenderYardLetter
import org.example.project.dashboard.DashboardTheme
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.service.YardDocumentSnapshot
import org.example.project.settings.LumaticaButton
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.fetch.RequestInit
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume

@Serializable
private data class ShareDocument(val id: String, val name: String, val needsRender: Boolean = false)
@Serializable
private data class ShareResponse(val ok: Boolean = false, val token: String = "", val documents: List<ShareDocument> = emptyList(), val url: String = "", val qr: String = "")
private val shareJson = Json { ignoreUnknownKeys = true }
private val activeDriverShares = mutableStateOf<Map<String, Pair<String, ShareResponse>>>(emptyMap())
internal fun clearDriverDocumentShares() { activeDriverShares.value = emptyMap() }

private suspend fun shareRequest(action: String, values: Map<String, String>): String {
    val response = window.fetch(
        "${LumaticaApiSettings.getBaseUrl()}/api/servicedocumentscreen/$action",
        RequestInit(method = "POST", headers = LumaticaApiManager.auditHeaders("application/json"),
            body = buildJsonObject { values.forEach { (k, v) -> put(k, v) } }.toString())
    ).await()
    if (!response.ok) error(if (response.status.toInt() == 409) "Für diesen Auftrag sind noch keine eindeutig zugeordneten Dokumente verfügbar." else "Freigabe nicht verfügbar. Bitte erneut versuchen.")
    return response.text().await()
}

private suspend fun pdfDataUrl(file: File): String = suspendCancellableCoroutine { continuation ->
    val reader = FileReader()
    reader.onload = {
        if (continuation.isActive) continuation.resume(reader.result as? String ?: "")
        null
    }
    reader.onerror = {
        if (continuation.isActive) continuation.cancel(IllegalStateException("PDF konnte nicht vorbereitet werden."))
        null
    }
    continuation.invokeOnCancellation { reader.abort() }
    reader.readAsDataURL(file)
}

@Composable
fun DriverDocumentSharing(card: DriverBookingCardModel, documentCount: Int) {
    val scope = rememberCoroutineScope()
    val key = "${card.providerFirmenId}:${card.artCode}:${card.booking.id}:${card.booking.lieferscheinnummer}"
    var busy by remember(key) { mutableStateOf(false) }
    var message by remember(key) { mutableStateOf("") }
    var opened by remember(key) { mutableStateOf(false) }
    val token = activeDriverShares.value[key]?.first.orEmpty()
    val share = activeDriverShares.value[key]?.second
    var snapshot by remember(key) { mutableStateOf<YardDocumentSnapshot?>(null) }
    var renderId by remember(key) { mutableStateOf("") }

    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(10.px); width(100.percent) } }) {
        Button(attrs = {
            classes(DriverOrderStyles.documentRow)
            attr("type", "button")
            onClick {
                opened = true
                if (share == null && !busy) {
                scope.launch {
                    busy = true
                    message = ""
                    var preparing = ""
                    try {
                            val created = shareJson.decodeFromString<ShareResponse>(shareRequest("share-create", mapOf(
                                "providerFirmenId" to card.providerFirmenId, "artCode" to card.artCode,
                                "auftragId" to card.booking.id.ifBlank { card.booking.lfdNr.toString() },
                                "lieferscheinnummer" to card.booking.lieferscheinnummer
                            )))
                            check(created.ok && created.token.isNotBlank())
                            preparing = created.token
                            for (doc in created.documents.filter { it.needsRender }) {
                                val raw = shareRequest("share-source", mapOf("token" to preparing, "documentId" to doc.id))
                                renderId = "share-render-${DateNow()}-${doc.id}"
                                snapshot = shareJson.decodeFromString<YardDocumentSnapshot>(raw)
                                val root = withTimeout(10000) {
                                    var element: HTMLElement? = null
                                    while (element == null) {
                                        delay(16)
                                        element = (document.getElementById(renderId) as? HTMLElement)
                                            ?.takeIf { it.querySelectorAll("[data-letter-pdf-page='true']").length > 0 }
                                    }
                                    element
                                }
                                val pdf = renderLetterDocumentPdfFile(root, doc.name)
                                shareRequest("share-upload", mapOf("token" to preparing, "documentId" to doc.id, "dataUrl" to pdfDataUrl(pdf)))
                                snapshot = null
                            }
                            val ready = shareJson.decodeFromString<ShareResponse>(shareRequest("share-activate", mapOf(
                                "token" to preparing, "origin" to window.location.origin
                            )))
                            check(ready.ok && ready.qr.isNotBlank())
                            activeDriverShares.value = activeDriverShares.value + (key to (preparing to ready.copy(documents = created.documents)))
                            preparing = ""
                    } catch (cancelled: CancellationException) {
                        throw cancelled
                    } catch (error: Throwable) {
                        message = error.message ?: "Freigabe konnte nicht erstellt werden."
                    } finally {
                        snapshot = null
                        busy = false
                        if (preparing.isNotBlank()) withContext(NonCancellable) {
                            runCatching { shareRequest("share-revoke", mapOf("token" to preparing)) }
                        }
                    }
                }
                }
            }
        }) {
            Img(src = "images/dokumente.svg", attrs = { classes(DriverOrderStyles.documentRowIcon) })
            Span({ classes(DriverOrderStyles.documentRowText) }) { Text("Dokumente teilen") }
        }
    }
    if (opened) {
        Div({
            attr("role", "dialog"); attr("aria-modal", "true"); attr("aria-label", "Dokumente teilen")
            style {
                position(Position.Fixed); property("inset", "0"); property("z-index", "120000")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                padding(24.px); boxSizing("border-box"); property("background", DashboardTheme.BACKGROUND_APP_GRADIENT)
            }
        }) {
            Div({ style {
                width(100.percent); maxWidth(360.px); display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(24.px); color(Color.white)
            } }) {
                Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(24.px); flexWrap(FlexWrap.Wrap) } }) {
                    Div({ classes(DriverOrderStyles.documentLinkRow) }) {
                        Img(src = "images/order.svg", attrs = { classes(DriverOrderStyles.documentRowIcon) })
                        Span { Text(card.booking.lieferscheinnummer.ifBlank { card.primaryRef }) }
                    }
                    Div({ classes(DriverOrderStyles.documentLinkRow) }) {
                        Img(src = "images/dokumente.svg", attrs = { classes(DriverOrderStyles.documentRowIcon) })
                        Span { Text((share?.documents?.size ?: documentCount).toString()) }
                    }
                }
                Div({
                    attr("aria-busy", busy.toString())
                    style { width(280.px); maxWidth(100.percent); property("aspect-ratio", "1"); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center) }
                }) {
                    share?.let { ready ->
                        Img(src = ready.qr, alt = "QR-Code") {
                            style { width(100.percent); height(100.percent); property("object-fit", "contain"); backgroundColor(Color.white); borderRadius(12.px) }
                        }
                    }
                    if (busy) Div({ attr("role", "progressbar"); attr("aria-label", "Dokumente teilen"); style {
                        width(28.px); height(28.px); border(3.px, LineStyle.Solid, rgba(255,255,255,0.2)); borderRadius(50.percent)
                        property("border-top-color", "white")
                    } })
                }
                if (message.isNotBlank()) P({ attr("role", "alert"); style { margin(0.px); color(Color("#ff9e9e")) } }) { Text(message) }
                LumaticaButton(label = "Teilen beenden", enabled = !busy, onClick = {
                    scope.launch {
                        busy = true
                        try {
                            if (token.isNotBlank()) shareRequest("share-revoke", mapOf("token" to token))
                            activeDriverShares.value = activeDriverShares.value - key
                            message = ""
                            opened = false
                        } catch (error: Throwable) {
                            message = error.message ?: "Freigabe konnte nicht beendet werden."
                        } finally { busy = false }
                    }
                })
            }
        }
    }
    snapshot?.let { s ->
        key(renderId) {
            Div({ style { position(Position.Fixed); left((-10000).px); top(0.px); width(794.px); property("pointer-events", "none"); property("opacity", "0"); backgroundColor(Color.white) } }) {
                RenderYardLetter(rootElementId = renderId, layoutKey = s.layoutKey, auftrag = s.auftrag,
                    vorgangslog = s.vorgangslog, unternehmen = s.unternehmen, logoSrc = s.logoSrc,
                    kontrollDefinitionen = s.kontrollDefinitionen, webKontrolle = s.webKontrolle,
                    ausstellerSignatur = s.ausstellerSignatur, ausstellerName = s.ausstellerName,
                    fahrerSignatur = s.fahrerSignatur, fahrerName = s.fahrerName)
            }
        }
    }
}

private fun DateNow(): String = js("Date.now().toString()") as String
